import queryString from "query-string";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";
import { setCurrentWorkspaceId } from "src/store/app";
import { handleInvitation } from "src/ui/containers/account/api";
import { LoadingIcon } from "src/ui/shared/Loading";
import { redirectTo } from "src/ui/utils/redirect";

import { showDetails } from "@dashboard/devices/store/index";
import { useLazyGetWorkspacesQuery } from "@dashboard/workspaces/api";
import { Box, Container, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { Logo } from "@shared/Logo/Logo";
import { TextLink } from "@shared/TextLink";

import { useStyles } from "./InvitationPage.style";
import { useCountdown } from "./useCountdown";

const InvitationPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState({ message: "", reason: "" });
    const [confirmationUrl, setConfirmationUrl] = useState<string | null>(null);

    const { classes } = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);

    const [getWorkspaces] = useLazyGetWorkspacesQuery();

    const { query } = queryString.parseUrl(window.location.href);

    const redirectToGroup = useCallback(async () => {
        try {
            const emptyId = query.id === undefined || query.id === null || query.id === "";
            const groupId = Number(query.id);
            if (emptyId || Number.isNaN(groupId)) {
                throw new Error(t("pages:invitationPage.groupNotFound"));
            }

            const [workspace] = await getWorkspaces({ groupId }).unwrap();
            if (!workspace) {
                throw new Error(t("pages:invitationPage.groupNotFound"));
            }

            dispatch(setCurrentWorkspaceId(workspace.id));
            dispatch(showDetails({ type: "group", selectedId: groupId }));

            navigate("/dashboard/devices");
        } catch (e) {
            const err = e as Error;
            const reason = err?.message ?? t("pages:invitationPage.unknownError");
            setError({
                message: t("pages:invitationPage.failedToRedirectToGroup"),
                reason: t("pages:invitationPage.reason", { reason }),
            });
        }
    }, [dispatch, query.id, navigate, getWorkspaces, t]);

    const redirectToWorkspace = useCallback(async () => {
        try {
            const emptyId = query.id === undefined || query.id === null || query.id === "";
            const workspaceId = Number(query.id);
            if (emptyId || Number.isNaN(workspaceId)) {
                throw new Error(t("pages:invitationPage.workspaceNotFound"));
            }
            const [workspace] = await getWorkspaces({ workspaceId }).unwrap();
            if (!workspace) {
                throw new Error(t("pages:invitationPage.workspaceNotFound"));
            }
            dispatch(setCurrentWorkspaceId(workspace.id));
            navigate("/account/workspaces");
        } catch (e) {
            const err = e as Error;
            const reason = err?.message ?? t("pages:invitationPage.unknownError");
            setError({
                message: t("pages:invitationPage.failedToRedirectToWorkspace"),
                reason: t("pages:invitationPage.reason", { reason }),
            });
        }
    }, [dispatch, navigate, query.id, getWorkspaces, t]);

    const handleRedirect = useCallback(async () => {
        if (confirmationUrl) {
            redirectTo(confirmationUrl);
        } else if (query.type === "group") {
            await redirectToGroup();
        } else if (query.type === "workspace") {
            await redirectToWorkspace();
        }
    }, [confirmationUrl, query.type, redirectToGroup, redirectToWorkspace]);

    const { countdown, start: startCountdown } = useCountdown(10, () => void handleRedirect());

    useEffect(() => {
        const processInvitation = async () => {
            if (
                typeof query.email === "string" &&
                typeof query.token === "string" &&
                typeof query.type === "string" &&
                typeof query.id === "string"
            ) {
                try {
                    const response = await handleInvitation({
                        email: query.email,
                        token: query.token,
                        type: query.type,
                        id: query.id,
                    });
                    if (response.status === 200) {
                        const { needEmailConfirmation, id: confirmationId, token: confirmationToken } = response.data.model;
                        if (needEmailConfirmation) {
                            const url =
                                "/account/confirm?id=" +
                                encodeURIComponent(confirmationId) +
                                "&token=" +
                                encodeURIComponent(confirmationToken) +
                                "&email=" +
                                encodeURIComponent(query.email);
                            setConfirmationUrl(url);
                        }
                    } else if (response.status === 201) {
                        redirectTo(response.headers.location as string);
                    }
                } catch (e) {
                    const err = e as Error;
                    setError({
                        message: t("pages:invitationPage.failedToAcceptInvitation"),
                        reason: t("pages:invitationPage.reason", { reason: err.message }),
                    });
                } finally {
                    setLoading(false);
                    startCountdown();
                }
            }
        };

        void processInvitation();
        // Do not add `t`: a language switch would re-run this and submit the single-use invitation token again.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, query.email, query.id, query.token, query.type, startCountdown]);

    return (
        <Container className={classes.root}>
            <Box className={classes.box}>
                <Logo variant="full" className={classes.logo} />
                {loading ? (
                    <Box className={classes.loadingBox}>
                        <LoadingIcon size="block" />
                    </Box>
                ) : (
                    <>
                        {error.message ? (
                            <>
                                <Typography variant="h6">{error.message}</Typography>
                                <Typography variant="h6" className={classes.topSpacing}>
                                    {error.reason}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6">{t("pages:invitationPage.yourInvitationAccepted")}</Typography>
                                <Typography variant="body1" className={classes.topSpacing}>
                                    {query.type === "group" ? (
                                        <Trans
                                            ns="pages"
                                            i18nKey="invitationPage.addedToGroup"
                                            components={{ span: <span className={classes.inviationTarget} /> }}
                                            values={{ name: query.name }}
                                        />
                                    ) : (
                                        <Trans
                                            ns="pages"
                                            i18nKey="invitationPage.addedToWorkspace"
                                            components={{ span: <span className={classes.inviationTarget} /> }}
                                            values={{ name: query.name }}
                                        />
                                    )}
                                </Typography>
                            </>
                        )}
                        <Typography variant="body1" className={classes.topSpacing}>
                            {t("pages:invitationPage.youWillBeRedirectedToConfirmationPageShortly")}
                        </Typography>
                        <Typography variant="body1" className={classes.countdown}>
                            {t("pages:invitationPage.redirectingIn", { countdown })}
                        </Typography>
                        <TextLink
                            className={classes.link}
                            onClick={handleRedirect}
                            tooltip={t("common:tooltip.goToConfirmation")}
                        >
                            {t("pages:invitationPage.moveNow")}
                        </TextLink>
                    </>
                )}
            </Box>
        </Container>
    );
};

export default withMetaDescription(InvitationPage, {
    title: i18next.t("pages:invitationPage.metaTitle"),
    description: i18next.t("pages:invitationPage.metaDescription"),
});
